/**
 * Organization Usage Dashboard
 *
 * Shows subscription usage metrics for organization admins
 * Requires permission: organization.usage.view
 */

import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import {
  ChartBarIcon,
  DocumentTextIcon,
  ClockIcon,
  UsersIcon,
  CalendarDaysIcon,
  ArrowDownTrayIcon,
  LockClosedIcon,
  ExclamationTriangleIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';
import { PageTransition, PageTransitionItem } from '@/components/ui/PageTransition';
import { UsageProgressBar } from '@/components/subscriptions/UsageProgressBar';
import { UserUsageTable } from '@/components/subscriptions/UserUsageTable';
import { useSubscriptionStore } from '@/stores/subscriptionStore';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

export const OrganizationUsageDashboard: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<'current' | 'last' | 'all'>('current');

  const {
    subscription,
    usage,
    usageLoading,
    usageError,
    userUsage,
    userUsageLoading,
    usageHistory,
    historyLoading,
    fetchSubscription,
    fetchUsage,
    fetchUserUsage,
    fetchUsageHistory
  } = useSubscriptionStore();

  // Check permission
  useEffect(() => {
    const checkPermission = async () => {
      if (!user) {
        setHasPermission(false);
        return;
      }

      // Admin and superadmin have access
      if (user.role === 'admin' || user.role === 'superadmin') {
        setHasPermission(true);
        return;
      }

      // Check explicit permission
      const permissions = user.permissions || [];
      const hasAccess = permissions.some(p =>
        p === 'organization.usage.view' ||
        p === 'organization.*'
      );
      setHasPermission(hasAccess);
    };

    if (!authLoading) {
      checkPermission();
    }
  }, [user, authLoading]);

  // Fetch data
  useEffect(() => {
    if (hasPermission) {
      fetchSubscription();
      fetchUsage();
      fetchUserUsage();
      fetchUsageHistory(6);
    }
  }, [hasPermission]);

  // Loading state
  if (authLoading || hasPermission === null) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50/30 flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Access denied
  if (!hasPermission) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50/30 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-8 max-w-md text-center"
        >
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <LockClosedIcon className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-slate-800 mb-2">Access Denied</h2>
          <p className="text-slate-600 mb-6">
            You don't have permission to view usage metrics. Contact your administrator.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Dashboard
          </button>
        </motion.div>
      </div>
    );
  }

  // Format period dates
  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const periodStart = subscription?.period.currentPeriodStart
    ? formatDate(subscription.period.currentPeriodStart)
    : 'N/A';
  const periodEnd = subscription?.period.currentPeriodEnd
    ? formatDate(subscription.period.currentPeriodEnd)
    : 'N/A';

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/api/v1/organization/subscription/usage/export`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `usage-report-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      }
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  return (
    <PageTransition>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50/30">
        {/* Header */}
        <div className="bg-white/60 backdrop-blur-sm border-b border-slate-200/50">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <PageTransitionItem delay={0.1}>
              <div className="flex justify-between items-center">
                <div>
                  <h1 className="text-2xl font-bold text-slate-800">Usage Dashboard</h1>
                  <p className="text-slate-600 mt-1">
                    Current Period: {periodStart} - {periodEnd}
                  </p>
                </div>
                <button
                  onClick={handleExport}
                  className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors text-slate-700"
                >
                  <ArrowDownTrayIcon className="w-5 h-5" />
                  Export CSV
                </button>
              </div>
            </PageTransitionItem>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-6">
          {/* Alert Banner */}
          {usage && (usage.isInOverage || usage.encounters.percentUsed >= 80 || usage.minutes.percentUsed >= 80) && (
            <PageTransitionItem delay={0.15}>
              <div className={`mb-6 p-4 rounded-xl border ${
                usage.shouldBlock
                  ? 'bg-red-50 border-red-200'
                  : usage.isInOverage
                  ? 'bg-amber-50 border-amber-200'
                  : 'bg-yellow-50 border-yellow-200'
              }`}>
                <div className="flex items-start gap-3">
                  <ExclamationTriangleIcon className={`w-6 h-6 flex-shrink-0 ${
                    usage.shouldBlock ? 'text-red-500' : usage.isInOverage ? 'text-amber-500' : 'text-yellow-500'
                  }`} />
                  <div>
                    <h3 className={`font-medium ${
                      usage.shouldBlock ? 'text-red-800' : usage.isInOverage ? 'text-amber-800' : 'text-yellow-800'
                    }`}>
                      {usage.shouldBlock
                        ? 'Subscription Limit Reached'
                        : usage.isInOverage
                        ? 'Overage Active'
                        : 'Approaching Usage Limit'}
                    </h3>
                    <p className={`text-sm mt-1 ${
                      usage.shouldBlock ? 'text-red-700' : usage.isInOverage ? 'text-amber-700' : 'text-yellow-700'
                    }`}>
                      {usage.shouldBlock
                        ? usage.blockReason || 'You have exceeded your subscription limits.'
                        : usage.isInOverage
                        ? `Current overage charges: $${usage.currentOverageAmount.toFixed(2)}`
                        : 'You have used 80% or more of your subscription limits.'}
                    </p>
                  </div>
                </div>
              </div>
            </PageTransitionItem>
          )}

          {/* Usage Cards */}
          <PageTransitionItem delay={0.2}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <UsageCard
                icon={<DocumentTextIcon className="w-6 h-6" />}
                label="Encounters"
                used={usage?.encounters.used || 0}
                limit={usage?.encounters.limit || 0}
                isUnlimited={usage?.encounters.isUnlimited || false}
                overage={usage?.encounters.overage || 0}
                color="blue"
              />
              <UsageCard
                icon={<ClockIcon className="w-6 h-6" />}
                label="Transcription Minutes"
                used={usage?.minutes.used || 0}
                limit={usage?.minutes.limit || 0}
                isUnlimited={usage?.minutes.isUnlimited || false}
                overage={usage?.minutes.overage || 0}
                color="emerald"
              />
              <UsageCard
                icon={<CurrencyDollarIcon className="w-6 h-6" />}
                label="Current Overage"
                value={`$${(usage?.currentOverageAmount || 0).toFixed(2)}`}
                color="amber"
                highlight={usage?.isInOverage}
              />
              <UsageCard
                icon={<CalendarDaysIcon className="w-6 h-6" />}
                label="Days Remaining"
                value={subscription?.period.currentPeriodEnd
                  ? Math.max(0, Math.ceil((new Date(subscription.period.currentPeriodEnd).getTime() - Date.now()) / (1000 * 60 * 60 * 24))).toString()
                  : 'N/A'
                }
                color="purple"
              />
            </div>
          </PageTransitionItem>

          {/* Detailed Usage */}
          <PageTransitionItem delay={0.3}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Encounters Progress */}
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-slate-200/50">
                <h3 className="text-lg font-semibold text-slate-800 mb-4">Encounter Usage</h3>
                <UsageProgressBar
                  used={usage?.encounters.used || 0}
                  limit={usage?.encounters.limit || 0}
                  label="Encounters this period"
                  isUnlimited={usage?.encounters.isUnlimited}
                  overage={usage?.encounters.overage}
                />
                {(usage?.encounters.overage || 0) > 0 && (
                  <div className="mt-4 p-3 bg-red-50 rounded-lg">
                    <p className="text-sm text-red-700">
                      <span className="font-medium">{usage?.encounters.overage} extra encounters</span>
                      {' '}at overage rates
                    </p>
                  </div>
                )}
              </div>

              {/* Minutes Progress */}
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-slate-200/50">
                <h3 className="text-lg font-semibold text-slate-800 mb-4">Transcription Minutes</h3>
                <UsageProgressBar
                  used={usage?.minutes.used || 0}
                  limit={usage?.minutes.limit || 0}
                  label="Minutes this period"
                  unit="min"
                  isUnlimited={usage?.minutes.isUnlimited}
                  overage={usage?.minutes.overage}
                />
                {(usage?.minutes.overage || 0) > 0 && (
                  <div className="mt-4 p-3 bg-red-50 rounded-lg">
                    <p className="text-sm text-red-700">
                      <span className="font-medium">{usage?.minutes.overage} extra minutes</span>
                      {' '}at overage rates
                    </p>
                  </div>
                )}
              </div>
            </div>
          </PageTransitionItem>

          {/* Usage History Chart */}
          <PageTransitionItem delay={0.4}>
            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-slate-200/50 mb-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Usage History</h3>
              {historyLoading ? (
                <div className="h-40 animate-pulse bg-slate-100 rounded"></div>
              ) : usageHistory.length === 0 ? (
                <div className="h-40 flex items-center justify-center text-slate-500">
                  No historical data available
                </div>
              ) : (
                <div className="h-40 flex items-end justify-between gap-2">
                  {usageHistory.slice(-6).map((period, index) => {
                    const maxEncounters = Math.max(...usageHistory.map(p => p.encounters), 1);
                    const height = (period.encounters / maxEncounters) * 100;

                    return (
                      <div key={period.periodKey} className="flex-1 flex flex-col items-center">
                        <div className="w-full flex flex-col items-center">
                          <span className="text-xs text-slate-600 mb-1">{period.encounters}</span>
                          <div
                            className={`w-full rounded-t transition-all ${
                              period.overageAmount > 0 ? 'bg-red-400' : 'bg-blue-400'
                            }`}
                            style={{ height: `${Math.max(height, 4)}%` }}
                          />
                        </div>
                        <span className="text-xs text-slate-500 mt-2">{period.periodKey}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </PageTransitionItem>

          {/* User Breakdown */}
          <PageTransitionItem delay={0.5}>
            <UserUsageTable
              users={userUsage}
              loading={userUsageLoading}
              totalEncounters={usage?.encounters.used}
              totalMinutes={usage?.minutes.used}
            />
          </PageTransitionItem>
        </div>
      </div>
    </PageTransition>
  );
};

// Usage Card Component
const UsageCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  used?: number;
  limit?: number;
  isUnlimited?: boolean;
  overage?: number;
  value?: string;
  color: 'blue' | 'emerald' | 'amber' | 'purple';
  highlight?: boolean;
}> = ({ icon, label, used, limit, isUnlimited, overage, value, color, highlight }) => {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    emerald: 'bg-emerald-100 text-emerald-600',
    amber: 'bg-amber-100 text-amber-600',
    purple: 'bg-purple-100 text-purple-600'
  };

  const formatNumber = (n: number) => {
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return n.toLocaleString();
  };

  return (
    <div className={`bg-white/80 backdrop-blur-sm rounded-xl p-4 border ${
      highlight ? 'border-amber-300 bg-amber-50/50' : 'border-slate-200/50'
    }`}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          {icon}
        </div>
        <span className="text-sm text-slate-600">{label}</span>
      </div>

      {value ? (
        <p className={`text-2xl font-bold ${highlight ? 'text-amber-600' : 'text-slate-800'}`}>
          {value}
        </p>
      ) : (
        <div>
          <div className="flex items-baseline gap-1">
            <span className="text-2xl font-bold text-slate-800">{formatNumber(used || 0)}</span>
            {!isUnlimited && (
              <span className="text-slate-500">/ {formatNumber(limit || 0)}</span>
            )}
            {isUnlimited && (
              <span className="text-emerald-600 text-sm ml-1">Unlimited</span>
            )}
          </div>
          {(overage || 0) > 0 && (
            <span className="text-xs text-red-600 font-medium">
              +{formatNumber(overage!)} overage
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default OrganizationUsageDashboard;
